import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import TagsInput from "@/components/core/forms/tags-input";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import Tooltip from "@/components/core/tooltip";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import eventService from "@/features/dashboard/api/services/event-service";
import { queryClient } from "@/query-client";
import { formatDate } from "@/utils/format-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconFile, IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import moment from "moment";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useCaseActivityHearingMutation } from "../api/queries/acitivities-query";
import caseEvidenceService from "../api/services/evidence-service";
import { type CaseActivity, type CaseRescheduleForm, caseRescheduleForm } from "../types/activities";

export default function CaseActivityRescheduleHearingModal({
	caseRecord,
	data,
	title,
}: {
	caseRecord: CaseRecord;
	title: string;
	data: CaseActivity;
}) {
	const { closeModal } = useModalControl();
	const caseActivitiesHearingMutation = useCaseActivityHearingMutation();
	const modal = useModal();
	const caseStageQuery = useSuspenseQuery(
		caseStagesQueryOptions({
			case_classification: caseRecord.case_classification ?? "",
		}),
	);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [parties, setParties] = useState<string>();
	const caseStagesNew = caseStageQuery.data;

	const form = useForm<CaseRescheduleForm>({
		resolver: zodResolver(caseRescheduleForm),
		defaultValues: {
			id: data?.id,
			status: title,
			case_id: data?.case_id,
			activity_id: data?.activity_id,
			assigned_to: data?.assigned_to || "Both",
			activity: "",
			party_type: "Bcc",
			hearing_status: "Rescheduled",
			title: data?.activity || "Hearing",
			type: data?.activity || "Hearing",
			remarks: data?.remarks || "",
			due_at: data?.due_at ? moment(data?.due_at).format("YYYY-MM-DDTHH:mm") : "",
			stage: data?.stage,
			hearing_mode: data?.hearing_mode || "",
			exhibits: data?.exhibits || [],
			witness_name: data?.witness_name || [],
			attachment: null,
		},
	});

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			setSelectedFiles((prevFiles) => {
				const newFiles = [...prevFiles, ...Array.from(files)];
				form.setValue("attachment", newFiles);
				setMultipleFiles(newFiles);
				return newFiles;
			});
		}
	};

	const handleRemoveAttachment = (index: number) => {
		setSelectedFiles((prevFiles) => {
			const updatedFiles = prevFiles.filter((_, i) => i !== index);
			form.setValue("attachment", updatedFiles);
			setMultipleFiles(updatedFiles);
			return updatedFiles;
		});
	};

	const storedTime = sessionStorage.getItem("password_entered_at");
	const handleSubmit = async (values: CaseRescheduleForm) => {
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			await caseActivitiesHearingMutation.mutateAsync(values);
			closeModal();
		} else {
			modal.openModal({
				allowClose: true,
				title: "Confirm Submit",
				description: "Are you sure? This action cannot be undone.",
				size: "sm",
				showHeader: true,
				showFooter: false,
				component: (
					<ConfirmWithPasswordModal
						onSubmit={async () => {
							await caseActivitiesHearingMutation.mutateAsync(values);
							closeModal();
						}}
					/>
				),
			});
		}
	};

	return (
		<Form {...form}>
			<form className="w-full h-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full h-full">
					<div className="border rounded-md w-full p-4 gap-4">
						<div className="flex flex-col">
							<div className="flex flex-row">
								<div className="flex flex-col w-full gap-3">
									<div className="flex flex-row items-center justify-between">
										<div className="flex flex-col w-full items-center justify-between">
											<span className="text-lg font-semibold text-foreground-dark">Reschedule Hearing</span>
										</div>
									</div>
									<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
										<div className="col-span-12 w-full">
											<Alert
												type="info"
												allowClose={false}
												title={`Previous Schedule: ${formatDate(data.due_at)}`}
												icon={<IconAlertCircle size={20} stroke={2} />}
											/>
										</div>
										<div className="col-span-3 w-full">
											<FormField
												control={form.control}
												name="due_at"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Date and Time</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Input
																type="datetime-local"
																value={field.value}
																onChange={async (e) => {
																	const value = e.target.value;
																	const response = await eventService.validateHearing(value, caseRecord.id);
																	form.setValue("hearing_count", response);
																	field.onChange(value);
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-3 w-full">
											<FormField
												control={form.control}
												name="hearing_count"
												render={({ field }) => {
													return (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel>Settings</FormLabel>
																<FormDescription />
															</div>
															<FormGroup>
																<Tooltip className="z-[999]" content="Click to open calendar in new tab" side="top">
																	{/* <button
                                      onClick={() => {
                                        window.open(`/calendar?view=true&date=${form.getValues(`hearings.${index}.due_date`)}`);
                                      }}
                                      type="button"
                                      className="text-start border w-full rounded-md block bg-muted text-muted-foreground px-3"
                                    > */}
																	<Button
																		className="w-full !justify-start !containerbg-muted text-muted-foreground"
																		variant="ghost"
																		onClick={() => {
																			window.open(`/calendar?view=true&date=${form.getValues(`hearings.${index}.due_date`)}`);
																		}}
																	>
																		{field.value || 0}
																	</Button>
																	{/* </button> */}
																</Tooltip>
															</FormGroup>
														</FormItem>
													);
												}}
											/>
										</div>
										<div className="col-span-3 w-full">
											<FormField
												control={form.control}
												name="stage"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Stage</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Select
																onValueChange={async (value) => {
																	field.onChange(value);
																}}
																defaultValue={field.value}
																options={caseStagesNew.data
																	.filter((c) => c.is_visible)
																	.map((c) => ({
																		label: c.label,
																		value: c.name,
																	}))}
															/>
														</FormGroup>
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-3 w-full">
											<FormField
												control={form.control}
												name="hearing_mode"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Hearing Mode</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Select
																onValueChange={async (value) => {
																	field.onChange(value);
																}}
																defaultValue={field.value}
																options={[
																	{
																		value: "Face to Face",
																		label: "Face to Face",
																	},
																	{
																		value: "Online",
																		label: "Online",
																	},
																]}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-12 w-full">
											<FormField
												control={form.control}
												name="remarks"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Activity/Event</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Input autoComplete="off" placeholder="e.g Presentation of Evidence" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 w-full">
											<FormField
												control={form.control}
												name="witness_name"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Witness</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<TagsInput
																isMulti={true}
																defaultValue={field.value}
																onValueChange={(value) => {
																	field.onChange(value);
																	if (value.length > 0) {
																		setParties(value[0].parties);
																	}
																}}
																loadOptions={async (value: string) => {
																	const items = await caseEvidenceService.listByCaseId(caseRecord.id, {
																		"filter[evidence_type]": "Testimonial",
																		"filter[case_id]": caseRecord.id,
																		"filter[search]": value,
																	});
																	return items.data.map((item) => ({
																		label: item.witness_name,
																		value: item.id,
																		parties: item.parties,
																		case_record_user_id: item.case_record_user_id || null,
																	}));
																}}
															/>
														</FormGroup>
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 w-full">
											<FormField
												control={form.control}
												name="exhibits"
												render={() => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Exhibits</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<FormField
																control={form.control}
																name="exhibits"
																render={({ field }) => (
																	<TagsInput
																		key={parties}
																		isMulti={true}
																		defaultValue={field.value}
																		onValueChange={(value) => {
																			field.onChange(value);
																		}}
																		loadOptions={async (value: string) => {
																			const items = await caseEvidenceService.listAllByCaseId(caseRecord.id, {
																				"filter[case_id]": caseRecord.id,
																				"filter[parties]": parties,
																				"filter[search]": value,
																			});
																			return items.data
																				.filter((i) => i.evidence_type !== "Testimonial")
																				.map((item) => ({
																					label: `${item.exhibit} - ${item.description}`,
																					value: item.id,
																					parties: item.parties,
																					witness_name: item.witness_name || null,
																					case_record_user_id: item.case_record_user_id || null,
																				}));
																		}}
																	/>
																)}
															/>
														</FormGroup>
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-12 w-full">
											<FormField
												control={form.control}
												name="attachment"
												render={() => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Proof of Payment</FormLabel>
														</div>
														<FormGroup>
															<div className="flex flex-col w-full rounded-md border border-border">
																<div className="relative flex flex-row items-center w-full h-[38px] max-h-[38px] p-1.5">
																	<input
																		type="file"
																		accept="application/pdf"
																		ref={inputRef}
																		className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
																		multiple
																		onChange={handleFileChange}
																	/>
																	<div className="flex flex-row items-center w-full gap-2">
																		<div className="inline-flex flex-row items-center px-3 h-[28px] gap-1 rounded bg-primary-600 font-semibold text-primary-foreground cursor-pointer">
																			<span className="text-sm">{multipleFiles.length > 0 ? "Add" : "Browse"}</span>
																		</div>
																		<span className="text-base text-foreground">
																			{multipleFiles.length === 1
																				? multipleFiles[0].name
																				: multipleFiles.length > 1
																					? "More than 2 files selected"
																					: "No file selected"}
																		</span>
																	</div>
																</div>

																{multipleFiles.length > 0 && (
																	<div className="flex flex-col w-full p-1.5 gap-2 border-t border-border">
																		{multipleFiles.map((file, index) => (
																			<div key={index} className="flex flex-row justify-between items-center">
																				<div className="items-center flex flex-row gap-2">
																					<div className="inline-flex shrink-0 justify-center items-center w-[32px] h-[32px] rounded-md bg-slate-200 text-foreground overflow-hidden">
																						<IconFile size={18} stroke={2} />
																					</div>
																					<div className="inline-flex flex-col">
																						<span className="truncate max-w-[240px] text-sm font-semibold text-foreground-dark">{file.name}</span>
																						<span className="text-sm text-foreground/50">{(file.size / 1024).toFixed(2)} KB</span>
																					</div>
																				</div>

																				<div className="inlineflex flex-row w-auto">
																					<Tooltip className="!z-[999]" content="Remove">
																						<button
																							className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-danger-600 hover:text-danger-500"
																							type="button"
																							color="red"
																							onClick={() => handleRemoveAttachment(index)}
																						>
																							<IconTrash size={18} stroke={2} />
																						</button>
																					</Tooltip>
																				</div>
																			</div>
																		))}
																	</div>
																)}
															</div>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="flex flex-row justify-between w-full mt-4">
						<Button
							className=""
							variant="ghost"
							type="button"
							onClick={() => {
								closeModal();
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={form.formState.isSubmitting} loading={form.formState.isSubmitting} variant="default">
							Confirm
						</Button>
					</div>
				</div>
			</form>
		</Form>
	);
}
